'use client';

import { type FC, Suspense, useEffect, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';
import { useShallow } from 'zustand/react/shallow';
import type { Wallet } from '@/api/auth';
import { amountWithCurrencySymbol } from '@/helpers/currency';
import { useComponentVisible } from '@/hooks/visibility';
import { useABTestingV2 } from '@/modules/abTesting';
import { AB_TESTS } from '@/modules/abTesting/constants';
import { useDepositStore, useGetCommonStore } from '@/store';
import { Spinner } from '@/styles/common';
import { IconComponent } from '../IconComponent';
import { SelectDepositContent } from './SelectDepositContent';
import { SelectDepositWithWagering } from './SelectDepositWithWagering';
import { ScSelectDeposit, type ScSelectDepositProps } from './styled';

type SelectDepositProps = Omit<ScSelectDepositProps, 'active'> & {
  noEditCurrencyBtn?: boolean;
  isDepositPanel?: boolean;
  togglePreventValidation?: (arg: boolean) => void;
};

export const SelectDeposit: FC<SelectDepositProps> = ({
  isInputAddon,
  isDepositPanel,
  withoutSum,
  noEditCurrencyBtn,
  togglePreventValidation,
  ...props
}) => {
  const { grayLightColor, mainColor, whiteColor } = useTheme();
  const useCommonStore = useGetCommonStore();

  const [checkUserData, toggleCheckUserData] = useState(false);
  const setOriginalAmount = useDepositStore.use.setOriginalAmount();
  const { currentWallet, changeUserWallet, pendingUser, pendingWallets } =
    useCommonStore(
      useShallow((state) => ({
        currentWallet: state.currentWallet,
        changeUserWallet: state.changeUserWallet,
        pendingUser: state.pendingUser,
        pendingWallets: state.pendingWallets,
      }))
    );

  const { ref, openerRef, isComponentVisible, setIsComponentVisible } =
    useComponentVisible(false);

  const { isB: isSeparatedWagering } = useABTestingV2({
    experiment: AB_TESTS.SEPARATED_WAGERING,
  });

  const pending = useMemo(
    () => pendingUser || pendingWallets,
    [pendingUser, pendingWallets]
  );

  const handleOpen = () => {
    setIsComponentVisible(!isComponentVisible);
  };

  const handleChange = async (currency: Wallet['currency']) => {
    if (!isSeparatedWagering) handleOpen();
    await changeUserWallet(currency);
    setOriginalAmount('');
    toggleCheckUserData(true);
  };

  useEffect(() => {
    if (!pending && checkUserData) {
      toggleCheckUserData(false);
    }
  }, [pending, checkUserData, toggleCheckUserData]);

  useEffect(() => {
    if (!togglePreventValidation) return;

    togglePreventValidation(isComponentVisible);
  }, [isComponentVisible, togglePreventValidation]);

  if (!currentWallet) return null;

  return (
    <ScSelectDeposit
      active={isComponentVisible}
      isInputAddon={isInputAddon}
      {...props}
    >
      <button
        className="select-button"
        disabled={pending}
        onClick={handleOpen}
        ref={openerRef}
        type="button"
      >
        {pending && !isInputAddon ? (
          <Spinner
            background={isInputAddon ? whiteColor : grayLightColor}
            center
            color={mainColor}
            size={20}
          />
        ) : (
          <>
            <div className="value">
              {withoutSum
                ? currentWallet.currency
                : amountWithCurrencySymbol(
                    currentWallet.subCurrency,
                    currentWallet.amountCents
                  )}
            </div>
            <div className="arrow">
              <IconComponent name="whiteArrowRight" size="16px" />
            </div>
          </>
        )}
      </button>
      {isComponentVisible && (
        <Suspense>
          {isSeparatedWagering && isDepositPanel ? (
            <SelectDepositWithWagering
              mode={props.mode}
              onChange={handleChange}
              ref={ref}
              toggleOpen={handleOpen}
            />
          ) : (
            <SelectDepositContent
              isInputAddon={isInputAddon}
              mode={props.mode}
              noEditCurrencyBtn={noEditCurrencyBtn}
              onChange={handleChange}
              onOpen={handleOpen}
              ref={ref}
            />
          )}
        </Suspense>
      )}
    </ScSelectDeposit>
  );
};
